import Api from '@/misc/api'
import type {
  Commit,
  CurrentSearchLike,
  ListOrphanPresentContentsJustificationsResponse,
  ListStayJustificationsResponse,
  ListStaySuggestionsGroupedByJustificationsResponse,
  PreviouslyDisplayedMcoStay,
  PreviouslyDisplayedSmrStay,
  SuggestionJustification,
} from '@/misc/types'
import type { RawMcoStay } from '@/store/modules/stay/mco/types'
import type { RawSmrStay } from '@/store/modules/stay/smr/types'
import { buildKeywordSearchParams } from '@/text-processing/utils'

// Card mode fetches this on-demand (switching display mode), unlike the flat justifications lookup
// which is prefetched alongside the stay — the grouping it returns is only needed once the user
// actually switches to the card view.
export function fetchGroupedSuggestionJustifications(stayId: string, commit: Commit): Promise<ListStaySuggestionsGroupedByJustificationsResponse> {
  const url = `/api/stay/${stayId}/suggestions/grouped-by-justifications?idType=id`

  return new Promise((resolve, reject) => {
    Api.get(
      {
        url,
        successMut: (data: ListStaySuggestionsGroupedByJustificationsResponse) => resolve(data),
        errorMut: () => reject(new Error(`Failed to fetch grouped suggestion justifications (${url})`)),
      },
      commit
    )
  })
}

// The suggestions-justifications route groups justifications by suggestion and by wildcard child,
// whereas the frontend needs to show child and parent justifications
// so this flattens the response into that lookup shape once, up-front.
export function flattenSuggestionJustifications(response: ListStayJustificationsResponse): Record<string, SuggestionJustification> {
  const flattened: Record<string, SuggestionJustification> = {}

  for (const suggestion of response.sancareSuggestions) {
    flattened[`${suggestion.type}:${suggestion.reference}`] = {
      textualJustifications: suggestion.textualJustifications,
      structuredJustifications: suggestion.structuredJustifications,
      chronicJustifications: suggestion.chronicJustifications,
      evaluations: suggestion.evaluations,
    }

    for (const child of suggestion.wildcardChildren) {
      flattened[`${suggestion.type}:${child.reference}`] = {
        textualJustifications: child.overrideParentTextualJustifications ? child.textualJustifications : suggestion.textualJustifications,
        structuredJustifications: child.overrideParentStructuredJustifications ? child.structuredJustifications : suggestion.structuredJustifications,
        // No override flag for chronic (unlike textual/structured): the backend already resolves
        // each child's own complete chronic list (see WildcardChildSuggestionDTO), not a delta.
        chronicJustifications: child.chronicJustifications,
        // Never inherited from the parent: an evaluation is about one code, a wildcard pattern's
        // own evaluations are not its children's.
        evaluations: child.evaluations,
      }
    }
  }

  return flattened
}

// Keeps, for each suggestion, only the evaluations of the given RUM and those of the whole stay: the
// same label can be evaluated on several RUMs of the stay, the other RUMs' evaluations are not about
// the displayed one. The RSS view (rumId 0) gathers every RUM, it keeps them all.
export function keepRumEvaluations(justifications: Record<string, SuggestionJustification>, rumId: number): Record<string, SuggestionJustification> {
  if (rumId === 0) {
    return justifications
  }
  return Object.fromEntries(Object.entries(justifications).map(([key, justification]) => [key, {
    ...justification,
    evaluations: justification.evaluations.filter((evaluation) => evaluation.rumId === null || evaluation.rumId === rumId),
  }]))
}

// Routed through Api.get (instead of a bare fetch) so a 401 triggers the same session-expiry
// retry, and a failure surfaces the same error toast, as every other API call in the app.
export function fetchJustifications(url: string, commit: Commit): Promise<ListStayJustificationsResponse> {
  return new Promise((resolve, reject) => {
    Api.get(
      {
        url,
        successMut: (data: ListStayJustificationsResponse) => resolve(data),
        errorMut: () => reject(new Error(`Failed to fetch stay justifications (${url})`)),
      },
      commit
    )
  })
}

// Routed through Api.post (instead of a bare fetch) so a 401 triggers the same session-expiry
// retry, and a failure surfaces the same error toast, as every other API call in the app.
// POST rather than GET: values/types come from an unbounded list of user-typed keywords, too large
// in the general case to fit safely in a query string.
function fetchKeywordJustifications(url: string, body: { values: string[], types: string[] }, commit: Commit): Promise<ListOrphanPresentContentsJustificationsResponse> {
  return new Promise((resolve, reject) => {
    Api.post(
      {
        url,
        body,
        successMut: (data: ListOrphanPresentContentsJustificationsResponse) => resolve(data),
        errorMut: () => reject(new Error(`Failed to fetch keyword justifications (${url})`)),
      },
      commit
    )
  })
}

/**
 * Kicks off the suggestions-justifications fetch alongside the stay/documents requests (threaded into
 * refreshDisplayedMcoStay as a fallback when no previously displayed stay can be reused).
 * includeBelowThreshold mirrors prefetchMcoStaySuggestions/prefetchMcoStayDocuments: the page séjour
 * passes true (frontend applies its own threshold), the staylist quick view passes false.
 */
export function prefetchMcoStayJustifications(idType: string, stayId: string, commit: Commit, includeBelowThreshold: boolean): Promise<ListStayJustificationsResponse> {
  return fetchJustifications(`/api/stay/${stayId}/justifications?idType=${idType}&includeBelowThreshold=${includeBelowThreshold ? 1 : 0}`, commit)
}

export function prefetchSmrStayJustifications(idType: string, stayId: string, commit: Commit): Promise<ListStayJustificationsResponse> {
  return fetchJustifications(`/api/smr/stay/${stayId}/justifications?idType=${idType}`, commit)
}

/**
 * Kicks off the orphan-keywords-justifications fetch alongside the stay/documents/justifications
 * requests, so it can run in parallel with (rather than only after) the stay request. Resolves to an
 * empty result without hitting the network when the search in progress has no keyword to look up.
 */
export function prefetchMcoKeywordJustifications(idType: string, stayId: string, currentSearch: CurrentSearchLike, commit: Commit): Promise<ListOrphanPresentContentsJustificationsResponse> {
  const { values, types } = buildKeywordSearchParams(currentSearch)
  if (values.length === 0) {
    return Promise.resolve({ presentContents: [] })
  }

  return fetchKeywordJustifications(`/api/stay/${stayId}/justifications/orphan-present-contents?idType=${idType}`, { values, types }, commit)
}

/**
 * The suggestions-justifications popin is a supplementary piece of the display, not a blocker like
 * the documents themselves: on failure it degrades to an empty lookup (the popin falls back to "no
 * justification") instead of preventing the stay from being shown.
 */
export async function getSuggestionJustifications(
  stay: RawMcoStay,
  commit: Commit,
  previousStay: PreviouslyDisplayedMcoStay | null,
  prefetched: Promise<ListStayJustificationsResponse> | null,
  includeBelowThreshold: boolean
): Promise<Record<string, SuggestionJustification>> {
  if (previousStay?.id === stay.id) {
    return previousStay.suggestionJustifications
  }

  try {
    const response = await (prefetched ?? fetchJustifications(`/api/stay/${stay.id}/justifications?idType=id&includeBelowThreshold=${includeBelowThreshold ? 1 : 0}`, commit))

    return flattenSuggestionJustifications(response)
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err)

    return {}
  }
}

export async function getSmrSuggestionJustifications(
  stay: RawSmrStay,
  commit: Commit,
  previousStay: PreviouslyDisplayedSmrStay | null,
  prefetched: Promise<ListStayJustificationsResponse> | null
): Promise<Record<string, SuggestionJustification>> {
  if (previousStay?.id === stay.id) {
    return previousStay.suggestionJustifications ?? {}
  }

  try {
    const response = await (prefetched ?? fetchJustifications(`/api/smr/stay/${stay.id}/justifications?idType=id`, commit))

    return flattenSuggestionJustifications(response)
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err)

    return {}
  }
}

/**
 * The keywords popin is a supplementary piece of the display, not a blocker like the documents
 * themselves: on failure it degrades to an empty lookup (the popin falls back to "no justification")
 * instead of preventing the stay from being shown.
 */
export async function getKeywordJustifications(
  stay: RawMcoStay,
  currentSearch: CurrentSearchLike,
  commit: Commit,
  previousStay: PreviouslyDisplayedMcoStay | null,
  prefetched: Promise<ListOrphanPresentContentsJustificationsResponse> | null
): Promise<Record<string, SuggestionJustification>> {
  if (previousStay?.id === stay.id) {
    return previousStay.keywordJustifications
  }

  const { ids, values, types } = buildKeywordSearchParams(currentSearch)
  if (values.length === 0) {
    return {}
  }

  try {
    const response = await (prefetched ?? fetchKeywordJustifications(`/api/stay/${stay.id}/justifications/orphan-present-contents?idType=id`, { values, types }, commit))

    return Object.fromEntries(ids.map((id, index) => [id, {
      textualJustifications: response.presentContents[index]?.textualJustifications ?? [],
      structuredJustifications: [],
      chronicJustifications: [],
      evaluations: [],
    }]))
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err)

    return {}
  }
}
